/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import {
  atom,
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from "recoil";
import { v4 as uuidv4 } from "uuid";
import { ContactApi } from "../../../api/Contacts";
import { ActiveEmailAtom } from "../../../atoms/ActiveEmailAtom";
import { RecipientAtom } from "../../../atoms/RecipientAtom";
import { CountedSavedSearchesAtom } from "../../../atoms/SavedSearches";
import { UserAtom } from "../../../atoms/UserAtom";
import { customTooltip } from "../../../styles/general";
import { IOption } from "../../../types/CommonTypes";
import { IRecipientTag } from "../../../types/SavedSearchTypes";
import { ITagQuery } from "../../../types/TagQueryTypes";
import { ITag } from "../../../types/TagTypes";
import { localStorageRemoveItem } from "../../../utils/LocalStorage";
import { recipientToTagQuery } from "../../../utils/TagQuery";
import Warning from "../../Modal/container/Warning";
import { useModal } from "../../Modal/Modal";
import SavedSearchSelect from "../../SavedSearch/SavedSearchSelect";
import { TooltipText } from "../../SideBar";
import StyledButton from "../../StyledButton";
import TagSelectRecipient from "../../TagSelect/TagSelectRecipient";
import SavedSearchesModal from "./SavedSearches";

const ModalBody = styled.div`
  width: 600px;
  height: 70vh;

  @media (max-height: 750px) {
    height: 80vh;
  }

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;

  border-top: 1px solid #eceeee;

  overflow-y: scroll;
  overflow-x: hidden;

  padding: 24px;
  margin: 20px -22px -25px;

  @media screen and (max-width: 1024px) {
    width: 100vw;
    height: calc(100vh - 75px);
    margin: 0;

    border-top: none;
  }
`;

const SavedSearches = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  margin-bottom: 30px;
`;

const TagRecipientWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  > div + div {
    margin-top: 16px;
  }
`;

const Title = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #333333;
  margin-bottom: 16px;
`;

const Buttons = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: space-between;

  border-top: 1px solid #eceeee;

  padding: 20px 24px 0px;

  width: calc(100% + 46px);
  margin: 0px -24px;
  margin-top: auto;

  @media screen and (max-width: 560px) {
    justify-content: center;

    button {
      min-width: fit-content !important;
      width: fit-content !important;
      padding: 10px !important;
    }
  }
`;

const AddCriteriaButton = styled.div`
  width: 100%;
  cursor: pointer;
  font-weight: 600;
  font-size: 16px;
  letter-spacing: 0px;
  color: #fb972e;

  transition: color 0.3s;
  padding: 20px 0px;

  :hover {
    color: #fb972e80;
  }
`;

const LogicButton = styled.div<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 32px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid;
  border-color: #cccccc;
  color: #979797;
  transition: color 0.3s, border-color 0.3s, opacity 0.3s;
  background-color: #ffffff;

  ${(props) =>
    props.isActive &&
    css`
      color: #fb972e;
      border: 1px solid #fb972e !important;
    `}

  :hover {
    opacity: 0.7;
  }
`;

const BorderContainer = styled.div`
  width: calc(100% + 50px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0px 24px;
  margin-bottom: 30px;
  border-bottom: 1px solid #eceeee;
`;

const LogicButtons = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: absolute;
  top: 23px;
`;

const NewRecipientTag = (): IRecipientTag => ({
  option: {
    name: "",
    value: "",
  },
  logic: { value: "AND", name: "And" },
  tags: [],
  id: uuidv4(),
});

// This will not work on edit mode! Only if I open
// and close the modal!
export const CurrentRecipientTagsAtom = atom({
  key: "CurrentRecipientTagsAtom",
  default: [NewRecipientTag()],
});

export default function Recipients(): JSX.Element {
  const modal = useModal();
  const setRecipientAtom = useSetRecoilState(RecipientAtom);
  const resetRecipientAtom = useResetRecoilState(RecipientAtom);
  const userInfo = useRecoilValue(UserAtom);
  const setActiveEmail = useSetRecoilState(ActiveEmailAtom);
  const [recipientTags, setRecipientTags] = useRecoilState<IRecipientTag[]>(
    CurrentRecipientTagsAtom
  );
  const [selectedSavedOption, setSelectedSavedOption] = useState<IOption>({
    value: "",
    name: "",
  });
  const [autoCompleteSuggestions, setAutoCompleteSuggestions] = useState<
    IOption[]
  >([]);
  const countedSavedSearches = useRecoilValue(CountedSavedSearchesAtom);
  const [isLoading, setIsLoading] = useState(false);

  async function addRecipientTags() {
    setRecipientTags([...recipientTags, NewRecipientTag()]);
  }

  const handleOptionRecipientTags = (option: IOption, index: number) => {
    const items = [...recipientTags];
    const item = { ...items[index] };
    item.option = { value: option.value, name: option.name };
    items[index] = item;

    if (option.value === "ALL") {
      item.tags = [];
    }
    setRecipientTags(items);
  };

  function handleLogicRecipientTags(logic: IOption, index: number) {
    const items = [...recipientTags];
    const item = { ...items[index] };
    item.logic = logic;
    items[index] = item;

    setRecipientTags(items);
  }

  function handleTagsRecipientTags(tags: ITag[], index: number) {
    const items = [...recipientTags];
    const item = { ...items[index] };
    item.tags = tags;
    items[index] = item;
    setRecipientTags(items);
  }

  function handleClearClick() {
    setRecipientTags([NewRecipientTag()]);
  }

  function removeRecipientTags(currentTag: IRecipientTag) {
    if (recipientTags.length > 1) {
      setRecipientTags(
        recipientTags.filter((recipientTag) => {
          return recipientTag.id !== currentTag.id;
        })
      );
    } else {
      toast("Must have at least one recipient", { type: "warning" });
    }
  }

  async function handleSaveRecipient() {
    if (!userInfo || isLoading) return;

    setIsLoading(true);
    let invalidTag = false;
    let invalidOption = false;

    const isRecipientTag =
      recipientTags[0].tags.length > 0 ||
      recipientTags[0].option.value === "ALL";

    for (let index = 0; index < recipientTags.length; index++) {
      const element = recipientTags[index];

      if (element.option.value !== "ALL" && element.tags.length === 0) {
        invalidTag = true;
      }

      if (element.option.value === "") {
        invalidOption = true;
      }
    }

    if (invalidOption) {
      toast("Select a valid filter option.", { type: "warning" });
      setIsLoading(false);

      return;
    }

    if (invalidTag) {
      toast("Select tags to filter.", { type: "warning" });
      setIsLoading(false);

      return;
    }

    if (isRecipientTag) {
      const tagQuery = recipientToTagQuery(recipientTags);

      const [contactsCount] = await ContactApi.countContactsByTagQueries([
        tagQuery,
      ]);

      setRecipientAtom({
        count: contactsCount,
        tagQuery,
        setted: "tagquery",
      });

      setActiveEmail(
        (prevActiveEmail) =>
          ({
            ...prevActiveEmail,
            tagQuery,
          } as any)
      );

      setRecipientTags(recipientTags);
      modal.popModal();
    } else {
      resetRecipientAtom();
      setRecipientTags(recipientTags);
      modal.popModal();
    }
    setIsLoading(false);
  }

  async function handleSaveCreatedSearch() {
    const tagQuery: ITagQuery = recipientToTagQuery(recipientTags);

    if (tagQuery.tags.length === 0) {
      toast("There must be at least one tag", { type: "info" });
      return;
    }

    modal.pushModal(<SavedSearchesModal tagQuery={tagQuery} />, "", true);
  }

  function selectedSearchModal(savedSearchOption: IOption) {
    if (recipientTags.length > 0 && recipientTags[0].tags.length > 0)
      modal.pushModal(
        <Warning
          title="Use Saved Search"
          description={`Are you sure you want to use your saved search “${savedSearchOption.name}”? You will lose the tags and conditions you have currently selected.`}
          buttonsName={["No", "Yes"]}
          icon={"./icons/modal/tagsFolder.svg"}
          onCancelClicked={modal.popModal}
          onOkClicked={() => handleSelectedSearch(savedSearchOption)}
        />
      );
    else {
      handleSelectedSearch(savedSearchOption);
    }
  }

  function handleSelectedSearch(savedSearchOption: IOption) {
    setSelectedSavedOption({
      name: savedSearchOption.name,
      value: savedSearchOption.name,
    });
    const selectedSavedSearch = countedSavedSearches.find(
      ({ id }) => savedSearchOption.value === id
    );

    if (!selectedSavedSearch) return;

    setRecipientAtom((prevSelected) => ({
      ...prevSelected,
      count: selectedSavedSearch.contactsCount,
      tagQuery: selectedSavedSearch.search,
      setted: "savedSearch",
      footerId: selectedSavedSearch.footerId,
      searchName: selectedSavedSearch.name,
      searchId: selectedSavedSearch.id,
    }));

    setRecipientTags([NewRecipientTag()]);
    modal.popAll();
  }

  // Update the suggestions
  useEffect(() => {
    const suggestions: IOption[] = countedSavedSearches.map((savedSearch) => ({
      value: savedSearch.id,
      name: savedSearch.name,
      icon: "tag",
      suffix: `${savedSearch.contactsCount}`,
    }));

    setAutoCompleteSuggestions(suggestions);
  }, [countedSavedSearches]);

  useEffect(() => {
    const listener = (event) => {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        event.preventDefault();
        handleSaveRecipient();
      }
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, [selectedSavedOption, recipientTags]);

  //If I have more than one tag criteria, then I go to the first one and change it
  //to "All Contacts", all the other ones goes bye bye
  useEffect(() => {
    if (recipientTags.length === 1) return;
    const firstOne = recipientTags[0];

    if (firstOne.option.value === "ALL") {
      setRecipientTags([firstOne]);
    }
  }, [recipientTags]);

  return (
    <ModalBody>
      <BorderContainer
        css={
          autoCompleteSuggestions.length === 0 &&
          css`
            border-bottom: none;
          `
        }
      >
        {autoCompleteSuggestions.length > 0 && (
          <SavedSearches>
            <Title>Saved Search</Title>
            <SavedSearchSelect
              options={autoCompleteSuggestions}
              onOptionSelect={selectedSearchModal}
              selectedOption={selectedSavedOption}
              placeholder="Type or select saved search"
            />
          </SavedSearches>
        )}
      </BorderContainer>

      <TagRecipientWrapper>
        {recipientTags.map((recipientTag, index) => (
          <>
            <TagSelectRecipient
              key={`recipients_recipient_tag_${recipientTag.id}`}
              recipientTag={recipientTag}
              index={index}
              removeRecipientTags={removeRecipientTags}
              handleOptionRecipientTags={handleOptionRecipientTags}
              handleTagsRecipientTags={handleTagsRecipientTags}
            />
            {recipientTags[index + 1] && (
              <div
                css={css`
                  position: relative;
                  margin: 0px -24px;
                  width: calc(100% + 50px);
                  border-bottom: 1px solid #eceeee;
                  height: 40px;
                  margin-top: 0px !important;
                  margin-bottom: 20px;
                `}
              >
                <LogicButtons>
                  <LogicButton
                    css={css`
                      border-radius: 4px 0px 0px 4px;
                      border-right: 1px solid #ffffff;
                    `}
                    isActive={recipientTag.logic.name === "And"}
                    onClick={() =>
                      handleLogicRecipientTags(
                        { name: "And", value: "AND" },
                        index
                      )
                    }
                  >
                    And
                  </LogicButton>
                  <LogicButton
                    css={css`
                      border-radius: 0px 4px 4px 0px;
                      border-left: 1px solid #ffffff;
                    `}
                    isActive={recipientTag.logic.name === "Or"}
                    onClick={() =>
                      handleLogicRecipientTags(
                        { name: "Or", value: "OR" },
                        index
                      )
                    }
                  >
                    Or
                  </LogicButton>
                </LogicButtons>
              </div>
            )}
          </>
        ))}
      </TagRecipientWrapper>

      {recipientTags[0].option.value !== "ALL" && (
        <AddCriteriaButton onClick={addRecipientTags}>
          + Add criteria
        </AddCriteriaButton>
      )}

      <Buttons>
        <div
          css={css`
            display: flex;
          `}
        >
          <StyledButton
            onClick={handleSaveCreatedSearch}
            inverted
            css={css`
              min-width: 128px;
              margin-right: 10px;
            `}
          >
            Save Search
          </StyledButton>

          <StyledButton
            onClick={handleClearClick}
            inverted
            css={css`
              width: 80px;
              margin-right: 10px;
            `}
          >
            Clear
          </StyledButton>
        </div>

        <div
          css={css`
            display: flex;
          `}
        >
          <StyledButton
            inverted
            onClick={() => modal.popModal()}
            css={css`
              width: 80px;
              margin-right: 10px;
            `}
          >
            Cancel
          </StyledButton>
          {/* <StyledButton
            onClick={handleSaveRecipient}
            disable={
              recipientTags.filter((r) => r.option.value === "").length > 0
            }
            css={css`
              width: 80px;
            `}
            isLoading={isLoading}
          >
            Apply
          </StyledButton> */}

          <div data-tip data-for="button-disabled">
            {recipientTags.filter((r) => r.option.value === "").length > 0 && (
              <ReactTooltip
                id="button-disabled"
                place="top"
                effect="solid"
                backgroundColor="#333333"
                css={customTooltip}
              >
                <TooltipText>Select a filter option first</TooltipText>
              </ReactTooltip>
            )}

            <StyledButton
              onClick={handleSaveRecipient}
              disable={
                recipientTags.filter((r) => r.option.value === "").length > 0
              }
              css={css`
                width: 80px;
              `}
              isLoading={isLoading}
            >
              Apply
            </StyledButton>
          </div>
        </div>
      </Buttons>
    </ModalBody>
  );
}
